import { animate, style, transition, trigger } from '@angular/animations'
import { ChangeDetectionStrategy, Component, computed, ElementRef, input, output, viewChild } from '@angular/core'
import { AmplitudeUnmaskDirective } from '../../directives'
import { isElementTruncated } from '../../util'
import { Icon, IconComponent } from '../icon'
import { TooltipModule } from '../tooltip'
import { SUBTLE_BUTTON_THEME_DISABLED, SUBTLE_BUTTON_THEMES, SubtleButtonThemeName } from './subtle-button.themes'

const hidden = { bottom: '-0.25rem' }
const visible = { bottom: '0' }
const timing = '0.2s ease'

/**
 * Small, understated button component designed for areas with limited space or multiple buttons
 * in close proximity.
 *
 * @example
 * ``` html
 * <ftr-subtle-button
 *   text="Start Sealing"
 *   icon="Locked"
 *   theme="sealing"
 *   [isLoading]="false"
 *   (onClick)="doSomething()"
 * />
 * ```
 */
@Component({
  selector: 'ftr-subtle-button',
  hostDirectives: [AmplitudeUnmaskDirective],
  templateUrl: './subtle-button.component.html',
  styleUrl: './subtle-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, TooltipModule],
  animations: [
    trigger('animateLoader', [
      transition(':enter', [style(hidden), animate(timing, style(visible))]),
      transition(':leave', [style(visible), animate(timing, style(hidden))]),
    ]),
  ],
})
export class SubtleButtonComponent {
  readonly text = input.required<string>()
  readonly theme = input<SubtleButtonThemeName>('default')
  readonly icon = input<Icon>()
  readonly isLoading = input(false)
  readonly disabled = input(false)
  readonly testId = input<string>()
  readonly tooltipText = input<string>()
  readonly ariaPressed = input<boolean>()
  readonly allowTruncation = input<boolean>(true)
  readonly trackGroup = input<string>()
  readonly type = input<HTMLButtonElement['type']>()

  readonly onClick = output()

  protected readonly themeValues = computed(() =>
    this.disabled() ? SUBTLE_BUTTON_THEME_DISABLED : SUBTLE_BUTTON_THEMES[this.theme()],
  )
  protected readonly requiresTooltip = computed(
    () => this.tooltipText() || (this.allowTruncation() && isElementTruncated(this.overflowListener().nativeElement)),
  )

  private readonly overflowListener = viewChild.required<ElementRef>('overflowListener')
}
